import React, { useEffect, useState } from 'react';
import WorkshopDetailListHeaderText from './WorkshopDetailListHeaderText';
import WorkshopDetailRow from './WorkshopDetailRow';
import WorkshopDetailListHeader from './WorkshopDetailListHeader';
import { Toast } from '@repo/common';
import { getSlotDetail } from '../../../api/workshop/workshop.api';

const WorkshopDetailList = ({
  confirmedList,
  waitlistedList,
  canceledList,
  blockedList,
  onStatsUpdate,
  selectedBookingIds,
  setSelectedBookingIds,
  slotId,
}) => {
  const [localConfirmedList, setLocalConfirmedList] = useState(confirmedList);
  const [localWaitlistedList, setLocalWaitlistedList] =
    useState(waitlistedList);
  const [localCanceledList, setLocalCanceledList] = useState(canceledList);
  const [localBlockedList, setLocalBlockedList] = useState(blockedList);

  const confirmedIds = localConfirmedList.map((i) => i.bookingId);
  const waitlistedIds = localWaitlistedList.map((i) => i.bookingId);
  const canceledIds = localCanceledList.map((i) => i.bookingId);
  const blockedIds = localBlockedList.map((i) => i.blockId);

  const [showWaitlistRemoveSuccessToast, setShowWaitlistRemoveSuccessToast] =
    useState(false);
  const [showBlockSuccessToast, setShowBlockSuccessToast] = useState(false);
  const [showCancelSuccessToast, setShowCancelSuccessToast] = useState(false);

  useEffect(() => {
    setLocalConfirmedList(confirmedList);
  }, [confirmedList]);
  useEffect(() => {
    setLocalWaitlistedList(waitlistedList);
  }, [waitlistedList]);
  useEffect(() => {
    setLocalCanceledList(canceledList);
  }, [canceledList]);
  useEffect(() => {
    setLocalBlockedList(blockedList);
  }, [blockedList]);

  const handleSelect = (type, id, checked) => {
    const key = `${type}-${id}`;

    setSelectedBookingIds((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(key);
      else newSet.delete(key);
      return newSet;
    });
  };

  const handleSelectAll = (checked, type) => {
    let ids = [];

    if (type === 'confirmed') ids = confirmedIds.map((id) => `confirmed-${id}`);
    if (type === 'waitlisted')
      ids = waitlistedIds.map((id) => `waitlisted-${id}`);
    if (type === 'canceled') ids = canceledIds.map((id) => `canceled-${id}`);
    if (type === 'blocked') ids = blockedIds.map((id) => `blocked-${id}`);

    setSelectedBookingIds((prev) => {
      const newSet = new Set(prev);

      if (checked) ids.forEach((id) => newSet.add(id));
      else ids.forEach((id) => newSet.delete(id));

      return newSet;
    });
  };

  const handleAttend = (bookingId, newStatus) => {
    setLocalConfirmedList((prev) =>
      prev.map((item) =>
        item.bookingId === bookingId ? { ...item, status: newStatus } : item,
      ),
    );

    onStatsUpdate?.((stats) => {
      const item = localConfirmedList.find((i) => i.bookingId === bookingId);
      const prevStatus = item?.status;
      let { attended, absent } = stats;
      if (prevStatus === 'ATTENDED') attended--;
      if (prevStatus === 'ABSENT') absent--;
      if (newStatus === 'ATTENDED') attended++;
      if (newStatus === 'ABSENT') absent++;
      return { ...stats, attended, absent };
    });
  };

  const handleBlock = (bookingId, listType, blockedEntry, canceledEntry) => {
    if (listType === 'confirmed') {
      const item = localConfirmedList.find((i) => i.bookingId === bookingId);
      const status = item?.status;

      if (status === 'ATTENDED' || status === 'ABSENT') {
        setLocalConfirmedList((prev) =>
          prev.map((i) =>
            i.bookingId === bookingId ? { ...i, blocked: true } : i,
          ),
        );
      } else {
        setLocalConfirmedList((prev) =>
          prev.filter((i) => i.bookingId !== bookingId),
        );
      }
    } else if (listType === 'waitlisted') {
      setLocalWaitlistedList((prev) =>
        prev.filter((i) => i.bookingId !== bookingId),
      );
    } else if (listType === 'canceled') {
      setLocalCanceledList((prev) =>
        prev.filter((i) => i.bookingId !== bookingId),
      );
    }

    if (canceledEntry) {
      setLocalCanceledList((prev) => [canceledEntry, ...prev]);
    }

    if (blockedEntry) {
      setLocalBlockedList((prev) => [blockedEntry, ...prev]);
    }

    if (canceledEntry) {
      onStatsUpdate?.((stats) => ({
        ...stats,
        confirmed: stats.confirmed - 1,
        canceled: stats.canceled + 1,
        left: stats.left + 1,
      }));
    }

    setShowBlockSuccessToast(false);
    setTimeout(() => setShowBlockSuccessToast(true), 10);
  };

  const handleUnblock = (blockId, updatedData) => {
    setLocalBlockedList((prev) =>
      prev.map((item) =>
        item.blockId === blockId
          ? { ...item, blockStatus: 'RELEASED', ...updatedData }
          : item,
      ),
    );
    if (!updatedData) {
      const unblocked = localBlockedList.find(
        (item) => item.blockId === blockId,
      );
      if (unblocked) {
        setLocalConfirmedList((prev) =>
          prev.map((item) =>
            item.userId === unblocked.userId
              ? { ...item, blocked: false }
              : item,
          ),
        );
      }
    }
  };

  const handleMvp = (bookingId, isMvp) => {
    setLocalConfirmedList((prev) =>
      prev.map((item) =>
        item.bookingId === bookingId ? { ...item, isMvp } : item,
      ),
    );
  };

  const refreshWaitlist = async () => {
    if (!slotId) return;
    try {
      const data = await getSlotDetail(slotId);
      setLocalWaitlistedList(data.waitlistedList ?? []);
    } catch (err) {
      console.error('Failed to refresh waitlist:', err);
    }
  };

  const handleCancel = (bookingId, canceledEntry) => {
    setLocalConfirmedList((prev) =>
      prev.filter((i) => i.bookingId !== bookingId),
    );
    if (canceledEntry) {
      setLocalCanceledList((prev) => [canceledEntry, ...prev]);
    }
    onStatsUpdate?.((stats) => ({
      ...stats,
      confirmed: stats.confirmed - 1,
      canceled: stats.canceled + 1,
      left: stats.left + 1,
    }));
    refreshWaitlist();
    setShowCancelSuccessToast(false);
    setTimeout(() => setShowCancelSuccessToast(true), 10);
  };

  const handleWaitlistRemove = (bookingId, canceledEntry) => {
    setLocalWaitlistedList((prev) => {
      const removedItem = prev.find((i) => i.bookingId === bookingId);
      const removedPosition = removedItem?.waitlistPosition;

      return prev
        .filter((i) => i.bookingId !== bookingId)
        .map((i) =>
          i.waitlistPosition > removedPosition
            ? { ...i, waitlistPosition: i.waitlistPosition - 1 }
            : i,
        );
    });

    if (canceledEntry) {
      setLocalCanceledList((prev) => [canceledEntry, ...prev]);
    }

    refreshWaitlist();

    setShowWaitlistRemoveSuccessToast(false);
    setTimeout(() => setShowWaitlistRemoveSuccessToast(true), 10);
  };

  const handleMoveWaitlist = (direction, bookingId) => {
    setLocalWaitlistedList((prev) => {
      const newList = [...prev];
      const index = newList.findIndex((i) => i.bookingId === bookingId);

      if (direction === 'UP' && index > 0) {
        [newList[index - 1], newList[index]] = [
          newList[index],
          newList[index - 1],
        ];
      }

      if (direction === 'DOWN' && index < newList.length - 1) {
        [newList[index + 1], newList[index]] = [
          newList[index],
          newList[index + 1],
        ];
      }

      return newList.map((item, idx) => ({
        ...item,
        waitlistPosition: idx + 1,
      }));
    });
  };

  const isAllSelected = (ids, type) =>
    ids.length > 0 &&
    ids.every((id) => selectedBookingIds.has(`${type}-${id}`));

  return (
    <div className='flex flex-col items-start w-full gap-8'>
      {/* Confirmed List */}
      <div className='flex flex-col items-start w-full gap-4'>
        <WorkshopDetailListHeaderText label={`Confirmed list`} />
        <div className='flex flex-col items-start h-[560px] p-6 pt-4 pr-2 rounded-[15px] bg-grayscale-gray00 w-full'>
          <WorkshopDetailListHeader
            type='confirmed'
            isAllSelected={isAllSelected(confirmedIds, 'confirmed')}
            onSelectAll={(checked) => handleSelectAll(checked, 'confirmed')}
          />
          <div
            className={`flex flex-col items-start ${
              localConfirmedList.length === 0
                ? 'items-center justify-center h-full'
                : 'items-start'
            } gap-2 w-full overflow-y-scroll max-h-[456px] pr-4
            [&::-webkit-scrollbar]:w-3.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-grayscale-gray04
            [&::-webkit-scrollbar-thumb]:rounded-[10px]
            [&::-webkit-scrollbar-thumb]:border-[4px]
            [&::-webkit-scrollbar-thumb]:border-solid
            [&::-webkit-scrollbar-thumb]:border-transparent
            [&::-webkit-scrollbar-thumb]:bg-clip-content`}
          >
            {[...localConfirmedList]
              .sort((a, b) => {
                if (a.isMvp && !b.isMvp) return -1;
                if (!a.isMvp && b.isMvp) return 1;
                return 0;
              })
              .map((item) => (
                <WorkshopDetailRow
                  key={item.bookingId}
                  item={item}
                  type='confirmed'
                  onAttend={handleAttend}
                  onBlock={(bookingId, blockedEntry, canceledEntry) =>
                    handleBlock(
                      bookingId,
                      'confirmed',
                      blockedEntry,
                      canceledEntry,
                    )
                  }
                  onMvp={handleMvp}
                  onCancel={handleCancel}
                  onRemoveWaitlist={handleWaitlistRemove}
                  checked={selectedBookingIds.has(
                    `confirmed-${item.bookingId}`,
                  )}
                  onSelect={(id, checked) =>
                    handleSelect('confirmed', id, checked)
                  }
                  onSelectAll={(checked) =>
                    handleSelectAll(checked, 'confirmed')
                  }
                />
              ))}
            {localConfirmedList.length === 0 && (
              <div className='typo-b14 text-text-placeholder flex flex-col items-center pb-6 w-full'>
                No confirmed participants yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Waitlisted List */}
      <div className='flex flex-col items-start w-full gap-4'>
        <WorkshopDetailListHeaderText label={`Waitlisted list`} />
        <div className='flex flex-col items-start h-[560px] p-6 pt-4 pr-2 rounded-[15px] bg-grayscale-gray00 w-full'>
          <WorkshopDetailListHeader
            type='waitlisted'
            isAllSelected={isAllSelected(waitlistedIds, 'waitlisted')}
            onSelectAll={(checked) => handleSelectAll(checked, 'waitlisted')}
          />
          <div
            className={`flex flex-col items-start ${
              localWaitlistedList.length === 0
                ? 'items-center justify-center h-full'
                : 'items-start'
            } gap-2 w-full overflow-y-scroll max-h-[456px] pr-4
          [&::-webkit-scrollbar]:w-3.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-grayscale-gray04
            [&::-webkit-scrollbar-thumb]:rounded-[10px]
            [&::-webkit-scrollbar-thumb]:border-[4px]
            [&::-webkit-scrollbar-thumb]:border-solid
            [&::-webkit-scrollbar-thumb]:border-transparent
            [&::-webkit-scrollbar-thumb]:bg-clip-content`}
          >
            {localWaitlistedList.map((item) => (
              <WorkshopDetailRow
                key={item.bookingId}
                item={item}
                type='waitlisted'
                onBlock={(bookingId, blockedEntry, canceledEntry) =>
                  handleBlock(
                    bookingId,
                    'waitlisted',
                    blockedEntry,
                    canceledEntry,
                  )
                }
                onRemoveWaitlist={handleWaitlistRemove}
                onMoveWaitlist={handleMoveWaitlist}
                onRefreshWaitlist={refreshWaitlist}
                checked={selectedBookingIds.has(`waitlisted-${item.bookingId}`)}
                onSelect={(id, checked) =>
                  handleSelect('waitlisted', id, checked)
                }
                onSelectAll={(checked) =>
                  handleSelectAll(checked, 'waitlisted')
                }
              />
            ))}
            {localWaitlistedList.length === 0 && (
              <div className='typo-b14 text-text-placeholder flex flex-col items-center pb-6'>
                No one is on the waitlist
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Canceled List */}
      <div className='flex flex-col items-start w-full gap-4'>
        <WorkshopDetailListHeaderText label={`Canceled list`} />
        <div className='flex flex-col items-start h-[400px] p-6 pt-4 pr-2 rounded-[15px] bg-grayscale-gray00 w-full'>
          <WorkshopDetailListHeader
            type='canceled'
            isAllSelected={isAllSelected(canceledIds, 'canceled')}
            onSelectAll={(checked) => handleSelectAll(checked, 'canceled')}
          />
          <div
            className={`flex flex-col ${
              localCanceledList.length === 0
                ? 'items-center justify-center h-full'
                : 'items-start'
            } gap-2 w-full overflow-y-scroll max-h-[296px] pr-4
          [&::-webkit-scrollbar]:w-3.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-grayscale-gray04
            [&::-webkit-scrollbar-thumb]:rounded-[10px]
            [&::-webkit-scrollbar-thumb]:border-[4px]
            [&::-webkit-scrollbar-thumb]:border-solid
            [&::-webkit-scrollbar-thumb]:border-transparent
            [&::-webkit-scrollbar-thumb]:bg-clip-content`}
          >
            {localCanceledList.map((item) => (
              <WorkshopDetailRow
                key={item.bookingId}
                item={item}
                type='canceled'
                onBlock={(bookingId, blockedEntry) =>
                  handleBlock(bookingId, 'canceled', blockedEntry, null)
                }
                onSelect={(id, checked) =>
                  handleSelect('canceled', id, checked)
                }
                checked={selectedBookingIds.has(`canceled-${item.bookingId}`)}
                onSelectAll={(checked) => handleSelectAll(checked, 'canceled')}
              />
            ))}
            {localCanceledList.length === 0 && (
              <div className='typo-b14 text-text-placeholder flex flex-col items-center pb-6'>
                No canceled bookings
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blocked List */}
      <div className='flex flex-col items-start w-full gap-4'>
        <WorkshopDetailListHeaderText label={`Blocked list`} />
        <div className='flex flex-col items-start h-[400px] p-6 pt-4 pr-2 rounded-[15px] bg-grayscale-gray00 w-full'>
          <WorkshopDetailListHeader
            type='blocked'
            isAllSelected={isAllSelected(blockedIds, 'blocked')}
            onSelectAll={(checked) => handleSelectAll(checked, 'blocked')}
          />
          <div
            className={`flex flex-col ${
              localBlockedList.length === 0
                ? 'items-center justify-center h-full'
                : 'items-start'
            } gap-2 w-full overflow-y-scroll max-h-[296px] pr-4
          [&::-webkit-scrollbar]:w-3.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-grayscale-gray04
            [&::-webkit-scrollbar-thumb]:rounded-[10px]
            [&::-webkit-scrollbar-thumb]:border-[4px]
            [&::-webkit-scrollbar-thumb]:border-solid
            [&::-webkit-scrollbar-thumb]:border-transparent
            [&::-webkit-scrollbar-thumb]:bg-clip-content`}
          >
            {localBlockedList.map((item) => (
              <WorkshopDetailRow
                key={item.blockId}
                item={item}
                type='blocked'
                onUnblock={handleUnblock}
                checked={selectedBookingIds.has(`blocked-${item.blockId}`)}
                onSelect={(id, checked) => handleSelect('blocked', id, checked)}
                onSelectAll={(checked) => handleSelectAll(checked, 'blocked')}
              />
            ))}
            {localBlockedList.length === 0 && (
              <div className='typo-b14 text-text-placeholder flex flex-col items-center pb-6'>
                No blocked users
              </div>
            )}
          </div>
        </div>
      </div>

      {showBlockSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full max-w-[266px] tabletH:w-[266px]'>
            <Toast
              title={'User blocked.'}
              message='You can view this in Blocked list.'
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      {showCancelSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full max-w-[226px] tabletH:w-[226px]'>
            <Toast
              title={'Booking canceled.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}

      {showWaitlistRemoveSuccessToast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full max-w-[259px] tabletH:w-[259px]'>
            <Toast
              title={'Removed from waitlist.'}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopDetailList;
